/**
 * Доска: карта и разметка поверх неё.
 *
 * Рисуется в SVG, а не на холсте. Фигуры хранятся списком, и SVG перерисовать
 * их умеет сам: не нужно ни следить за перерисовкой при изменении размера
 * окна, ни держать второй слой ради предпросмотра стрелки. Холст появляется
 * один раз — при выгрузке в PNG, и рисует по тому же списку.
 *
 * Указатель обрабатывается событиями `pointer*`, а не мышью: те же жесты
 * работают пальцем на планшете, куда тактику как раз и открывают.
 */

import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import type { TacticArrow, TacticArrowVariant, TacticPoint, TacticShape, TacticStroke } from '@ak/shared';

import { carImageUrl } from '../lib/car-images.ts';
import {
  CAR_BOX,
  CAR_LABEL,
  carAngle,
  carSize,
  layoutCarLabels,
  moveCarLabel,
  moveShape,
  nextMarkerLabel,
  pickCarLabelAt,
  type CarLabelBox,
  pickShapeAt,
  readableInk,
  rollMarkerLabel,
  shouldCommitDrag,
  toUnitPoint,
} from '../lib/tactics.ts';

/**
 * Чем сейчас рисуют.
 *
 * Два вида стрелок разведены в отдельные инструменты, а не в переключатель
 * при одной: на капте «идём туда» и «стреляем туда» — разные приказы, и
 * промахнуться переключателем значит увести состав не туда.
 */
export type TacticTool = 'hand' | 'move' | 'fire' | 'brush' | 'dashed' | 'marker' | 'car' | 'eraser';

/**
 * Ширина системы координат слоя разметки.
 *
 * Само число ни на что не влияет — слой растягивается под доску. Важно, что
 * оно одно для координат, толщин и кеглей: в долях от нуля до единицы линия
 * толщиной 0,004 была бы тоньше пикселя.
 */
const VIEW = 1000;

export interface TacticBoardProps {
  /** Адрес картинки карты. */
  imageUrl: string;
  mapName: string;
  /** Пропорции карты: доска держит их до загрузки картинки. */
  ratio: number;
  shapes: readonly TacticShape[];
  tool: TacticTool;
  color: string;
  width: number;
  /** Разметку не показывать — посмотреть карту без стрелок. */
  hidden: boolean;
  /** Плашки с никами не показывать: карта с одними машинами читается чище. */
  hideCrew: boolean;
  /** Нажали рукой по машине, не сдвинув её: пора править экипаж. */
  onEditCar?: (index: number) => void;
  /** Кого сажать в следующую машину: ники, выбранные в панели состава. */
  crew: readonly string[];
  /**
   * Сколько высоты окна занято не доской: шапка страницы, поля панели.
   * Приходит снаружи, потому что на весь экран над доской нет ничего.
   */
  reserve?: string;
  readOnly?: boolean;
  /**
   * Правка разметки.
   *
   * `mergeKey` — признак «это продолжение того же действия»: подряд идущие
   * правки с одним ключом складываются в один шаг отмены. Нужен колесу на
   * метке: иначе Ctrl+Z откатывал бы номер по единице.
   */
  onChange: (shapes: TacticShape[], mergeKey?: string) => void;
}

/** Насколько близко к фигуре надо ткнуть рукой, чтобы её взять. */
const GRAB_RADIUS = 0.035;

const MIN_SCALE = 1;
const MAX_SCALE = 6;

export function TacticBoard({
  imageUrl,
  mapName,
  ratio,
  shapes,
  tool,
  color,
  width,
  hidden,
  hideCrew,
  crew,
  reserve = '10rem',
  onChange,
  onEditCar,
}: TacticBoardProps): ReactNode {
  const boxRef = useRef<HTMLDivElement | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  // Фигура, которую тянут прямо сейчас: она видна, но в список ещё не легла.
  // Тянутся только маркер и стрелки — метка и машина ставятся одним нажатием.
  const [draft, setDraft] = useState<TacticStroke | TacticArrow | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'failed'>('loading');
  const drawing = useRef(false);

  /*
   * Камера: во сколько раз приближено и на сколько сдвинуто.
   *
   * Сдвиг — в пикселях доски до увеличения, начало отсчёта в левом верхнем
   * углу. В этих же единицах его считает колесо, поэтому точка под курсором
   * остаётся на месте при приближении.
   */
  const [camera, setCamera] = useState({ scale: 1, x: 0, y: 0 });

  /*
   * Что сейчас таскают рукой.
   *
   * Пока фигуру двигают, её новое положение живёт в `moved` и наружу не
   * уходит: иначе каждое движение мыши ложилось бы в историю отмены отдельным
   * шагом, и Ctrl+Z разбирал бы один перенос сотней нажатий.
   */
  const dragging = useRef<{
    mode: 'shape' | 'label' | 'pan';
    index: number;
    from: TacticPoint;
    origin: { x: number; y: number };
    /**
     * Где стояла плашка, когда её взяли.
     *
     * Запоминается один раз: раскладка плашек во время перетаскивания уже
     * учитывает начатый сдвиг, и брать основу из неё значит прибавлять
     * смещение к самому себе.
     */
    labelFrom: TacticPoint | null;
  } | null>(null);
  /*
   * Перенос держится и в состоянии, и в ссылке.
   *
   * Состояние нужно, чтобы доска перерисовывалась, а ссылка — чтобы в момент
   * отпускания взять готовый результат, не заглядывая в обновитель. Обновитель
   * состояния обязан быть чистым: React вправе вызвать его дважды, и позвать
   * оттуда onChange значило бы дважды записать шаг в историю отмены.
   */
  const [moved, setMoved] = useState<TacticShape[] | null>(null);
  const movedRef = useRef<TacticShape[] | null>(null);
  const draftRef = useRef<TacticStroke | TacticArrow | null>(null);

  useEffect(() => {
    const image = imageRef.current;
    setState(image !== null && image.complete && image.naturalWidth > 0 ? 'ready' : 'loading');
  }, [imageUrl]);

  /** Точка карты под курсором — с поправкой на приближение и сдвиг. */
  function pointAt(clientX: number, clientY: number): TacticPoint {
    const box = boxRef.current?.getBoundingClientRect();
    if (box === undefined) {
      return [0, 0];
    }

    return toUnitPoint(
      (clientX - box.left - camera.x) / camera.scale,
      (clientY - box.top - camera.y) / camera.scale,
      { left: 0, top: 0, width: box.width, height: box.height },
    );
  }

  /** Сдвиг камеры не должен выпускать карту из окна доски. */
  function clampCamera(next: { scale: number; x: number; y: number }): { scale: number; x: number; y: number } {
    const box = boxRef.current?.getBoundingClientRect();
    if (box === undefined) {
      return next;
    }

    const minX = box.width * (1 - next.scale);
    const minY = box.height * (1 - next.scale);

    return { scale: next.scale, x: Math.min(0, Math.max(minX, next.x)), y: Math.min(0, Math.max(minY, next.y)) };
  }

  /**
   * Колесо: над меткой меняет её номер, под рукой приближает карту.
   *
   * Номер правят прямо на карте, потому что чаще всего это нужно сразу после
   * того, как метка поставлена, — идти за ней в список было бы дольше.
   *
   * Приближение — только когда выбрана рука: рисуя стрелку, колесо крутят,
   * чтобы прокрутить страницу, и карта прыгала бы под курсором.
   */
  function handleWheel(event: WheelEvent): void {
    const point = pointAt(event.clientX, event.clientY);

    if (!hidden) {
      const index = pickShapeAt(shapes, point, GRAB_RADIUS);
      const shape = index === null ? undefined : shapes[index];

      if (index !== null && shape !== undefined && shape.kind === 'marker') {
        event.preventDefault();
        onChange(
          shapes.map((item, position) =>
            position === index && item.kind === 'marker'
              ? { ...item, label: rollMarkerLabel(item.label, event.deltaY < 0 ? 1 : -1) }
              : item,
          ),
          // Один шаг отмены на всю прокрутку: откат возвращает номер, который
          // был до того, как за колесо взялись.
          `marker:${String(index)}`,
        );

        return;
      }
    }

    const box = boxRef.current?.getBoundingClientRect();
    if (tool !== 'hand' || box === undefined) {
      return;
    }

    event.preventDefault();

    const factor = event.deltaY < 0 ? 1.2 : 1 / 1.2;
    const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, camera.scale * factor));
    const change = scale / camera.scale;
    const cursorX = event.clientX - box.left;
    const cursorY = event.clientY - box.top;

    setCamera(
      clampCamera({
        scale,
        x: cursorX - (cursorX - camera.x) * change,
        y: cursorY - (cursorY - camera.y) * change,
      }),
    );
  }

  /*
   * Колесо слушается напрямую, а не через React.
   *
   * React вешает обработчик колеса пассивным, и отменить прокрутку страницы
   * из него нельзя: карта приближалась бы, а страница одновременно уезжала
   * вниз. Ссылка на свежий обработчик держится отдельно, чтобы слушателя
   * не переподключать на каждую перерисовку.
   */
  const wheelRef = useRef(handleWheel);
  wheelRef.current = handleWheel;

  useEffect(() => {
    const box = boxRef.current;
    if (box === null) {
      return;
    }

    const listener = (event: WheelEvent): void => {
      wheelRef.current(event);
    };

    box.addEventListener('wheel', listener, { passive: false });
    return () => {
      box.removeEventListener('wheel', listener);
    };
  }, []);

  function handleDown(event: ReactPointerEvent<HTMLDivElement>): void {
    if (hidden || event.button !== 0) {
      return;
    }

    const point = pointAt(event.clientX, event.clientY);

    if (tool === 'hand') {
      event.currentTarget.setPointerCapture(event.pointerId);

      /*
       * Сначала плашки, потом фигуры: плашки лежат поверх, и нажатие на ники
       * должно брать их, а не то, что нарисовано под ними.
       */
      const label = hideCrew ? null : pickCarLabelAt(labels, point);
      const index = label === null ? pickShapeAt(shapes, point, GRAB_RADIUS) : label.index;

      // Взяли плашку — двигаем её, взяли фигуру — фигуру, попали в пустое
      // место — двигаем карту.
      dragging.current = {
        mode: label !== null ? 'label' : index === null ? 'pan' : 'shape',
        index: index ?? -1,
        from: point,
        origin: { x: event.clientX - camera.x, y: event.clientY - camera.y },
        labelFrom: label === null ? null : [label.x, label.y],
      };

      return;
    }

    if (tool === 'eraser') {
      const index = pickShapeAt(shapes, point);
      if (index !== null) {
        onChange(shapes.filter((_, position) => position !== index));
      }

      return;
    }

    if (tool === 'marker') {
      onChange([...shapes, { kind: 'marker', color, at: point, label: nextMarkerLabel(shapes) }]);
      return;
    }

    if (tool === 'car') {
      /*
       * Следующая картинка по кругу: машины на доске должны отличаться друг от
       * друга с одного взгляда. Отсчёт по наибольшему номеру, а не по
       * количеству — иначе после стирания второй машины третья получила бы
       * картинку уже стоящей. Ровно то же правило, что у номеров меток.
       */
      const image = shapes.reduce((max, shape) => (shape.kind === 'car' ? Math.max(max, shape.image + 1) : max), 0);
      onChange([...shapes, { kind: 'car', color, at: point, image, crew: [...crew], labelAt: null }]);
      return;
    }

    // Маркер и стрелки тянутся: указатель захватывается, чтобы линия не
    // обрывалась, когда курсор вышел за край карты.
    drawing.current = true;
    event.currentTarget.setPointerCapture(event.pointerId);

    const variant: TacticArrowVariant = tool === 'fire' ? 'fire' : 'move';

    const started: TacticStroke | TacticArrow =
      tool === 'brush' || tool === 'dashed'
        ? { kind: 'stroke', color, width, dashed: tool === 'dashed', points: [point[0], point[1]] }
        : { kind: 'arrow', variant, color, width, from: point, to: point };

    draftRef.current = started;
    setDraft(started);
  }

  function handleMove(event: ReactPointerEvent<HTMLDivElement>): void {
    const grab = dragging.current;

    if (grab !== null) {
      if (grab.mode === 'pan') {
        // Габариты доски читаются здесь, а не в обновителе состояния: читать
        // разметку страницы во время рендера нельзя.
        setCamera(
          clampCamera({ scale: camera.scale, x: event.clientX - grab.origin.x, y: event.clientY - grab.origin.y }),
        );

        return;
      }

      const point = pointAt(event.clientX, event.clientY);
      const dx = point[0] - grab.from[0];
      const dy = point[1] - grab.from[1];

      const next = shapes.map((shape, index) => {
        if (index !== grab.index) {
          return shape;
        }

        if (grab.mode !== 'label' || shape.kind !== 'car') {
          return moveShape(shape, dx, dy);
        }

        // Плашка двигается отдельно от машины: её место запоминается в самой
        // машине, поэтому переживает сохранение дефолта. Основа — там, где
        // плашку взяли, и взята она один раз: см. `moveCarLabel`.
        return moveCarLabel(shape, grab.labelFrom, dx, dy);
      });

      movedRef.current = next;
      setMoved(next);

      return;
    }

    if (!drawing.current) {
      return;
    }

    const point = pointAt(event.clientX, event.clientY);

    const current = draftRef.current;
    if (current === null) {
      return;
    }

    if (current.kind === 'stroke') {
      const last = current.points.length;
      const previousX = current.points[last - 2] ?? 0;
      const previousY = current.points[last - 1] ?? 0;

      // Точки ближе трети процента карты друг к другу ничего не добавляют
      // к линии, зато раздувают пресет: рука за секунду даёт их сотни.
      if (Math.hypot(point[0] - previousX, point[1] - previousY) < 0.003) {
        return;
      }

      const grown = { ...current, points: [...current.points, point[0], point[1]] };
      draftRef.current = grown;
      setDraft(grown);

      return;
    }

    const stretched = { ...current, to: point };
    draftRef.current = stretched;
    setDraft(stretched);
  }

  function handleUp(): void {
    const grab = dragging.current;

    if (grab !== null) {
      dragging.current = null;

      const result = movedRef.current;
      movedRef.current = null;
      setMoved(null);

      // Перенос ложится в историю одним шагом — уже после того, как отпустили.
      if (shouldCommitDrag(grab.mode, result !== null) && result !== null) {
        onChange(result);

        return;
      }

      /*
       * Взяли машину и отпустили, не сдвинув, — это не перенос, а «открой её».
       * Править экипаж нажатием по самой машине понятнее, чем искать нужную
       * строку в списке: на доске видно, какая где стоит.
       */
      if (grab.mode === 'shape') {
        const shape = shapes[grab.index];
        if (shape !== undefined && shape.kind === 'car') {
          onEditCar?.(grab.index);
        }
      }

      return;
    }

    if (!drawing.current) {
      return;
    }

    drawing.current = false;

    const current = draftRef.current;
    draftRef.current = null;
    setDraft(null);

    if (current === null) {
      return;
    }

    // Клик без движения — не фигура: точка вместо линии и стрелка нулевой
    // длины появлялись бы от каждого случайного тычка.
    const meaningful =
      current.kind === 'stroke'
        ? current.points.length >= 4
        : Math.hypot(current.to[0] - current.from[0], current.to[1] - current.from[1]) > 0.01;

    if (meaningful) {
      onChange([...shapes, current]);
    }
  }

  const drawn = moved ?? shapes;
  const visible = hidden ? [] : [...drawn, ...(draft === null ? [] : [draft])];
  const labels = hidden || hideCrew ? [] : layoutCarLabels(visible, ratio);

  return (
    <div
      ref={boxRef}
      className="relative mx-auto w-full shrink-0 touch-none overflow-hidden rounded-2xl border select-none"
      style={{
        aspectRatio: String(ratio),
        /*
         * `shrink-0` в классах обязателен: доска лежит во флексе, и без него
         * колонка сжимает её по высоте. Коробка перестаёт повторять пропорции
         * карты, картинка внутри letterbox-ится, а слой разметки тянется по
         * коробке — рисунок уезжает с карты.
         *
         * Карта должна помещаться в окно целиком: тактику смотрят на неё
         * всю сразу, а листать доску, рисуя стрелку, невозможно. Карты
         * каптов квадратные, и без ограничения доска во всю ширину панели
         * уходила на полторы тысячи пикселей вниз.
         *
         * Ограничение задано шириной, а не высотой: слой разметки лежит
         * поверх коробки, и если коробка перестанет повторять пропорции
         * картинки, рисунок съедет относительно карты.
         *
         * Запас — это всё, что на экране занято не доской; он приходит
         * снаружи, потому что на весь экран над доской ничего нет. Подобран
         * замером на экране 1920×1080: при меньшем запасе низ доски уходил
         * под край окна.
         */
        maxWidth: `calc((100vh - ${reserve}) * ${String(ratio)})`,
        borderColor: 'var(--border-subtle)',
        background: 'var(--surface-page)',
        cursor: hidden ? 'default' : tool === 'hand' ? 'grab' : tool === 'eraser' ? 'cell' : 'crosshair',
      }}
      onPointerDown={handleDown}
      onPointerMove={handleMove}
      onPointerUp={handleUp}
      onPointerCancel={handleUp}
    >
      {/*
       * Карта и разметка едут вместе: приближение и сдвиг заданы одним
       * преобразованием на общей обёртке, иначе рисунок отставал бы от карты
       * на каждый шаг колеса.
       */}
      <div
        className="absolute inset-0"
        style={{
          transform: `translate(${String(camera.x)}px, ${String(camera.y)}px) scale(${String(camera.scale)})`,
          transformOrigin: '0 0',
        }}
      >
        <img
          ref={imageRef}
          src={imageUrl}
          alt={`Карта «${mapName}»`}
          className="absolute inset-0 h-full w-full object-contain"
          draggable={false}
          onLoad={() => {
            setState('ready');
          }}
          onError={() => {
            setState('failed');
          }}
        />

        <svg
          viewBox={`0 0 ${String(VIEW)} ${String(Math.round(VIEW / ratio))}`}
          className="pointer-events-none absolute inset-0 h-full w-full"
          aria-hidden="true"
        >
          {visible.map((shape, index) => (
            <ShapeView key={index} shape={shape} height={VIEW / ratio} />
          ))}

          {/*
           * Плашки экипажей — отдельным слоем поверх фигур: их раскладка
           * считается на всю доску сразу, а лежащую под чужой стрелкой плашку
           * не прочитать.
           */}
          {labels.map((label) => (
            <CarLabelView key={label.index} label={label} height={VIEW / ratio} />
          ))}
        </svg>
      </div>

      {state !== 'ready' && (
        <div
          className="absolute inset-0 flex items-center justify-center px-6 text-center text-sm"
          style={{ color: 'var(--text-muted)' }}
        >
          {state === 'loading'
            ? 'Карта загружается…'
            : `Картинка карты «${mapName}» не открылась. Проверьте файл в server/assets/tactic-maps.`}
        </div>
      )}

      {camera.scale > 1 && (
        <button
          type="button"
          className="absolute right-3 bottom-3 cursor-pointer rounded-lg border px-2.5 py-1 text-xs"
          style={{
            background: 'var(--surface-panel)',
            borderColor: 'var(--border-subtle)',
            color: 'var(--text-secondary)',
          }}
          onClick={() => {
            setCamera({ scale: 1, x: 0, y: 0 });
          }}
        >
          {Math.round(camera.scale * 100)}% · сбросить
        </button>
      )}
    </div>
  );
}

/**
 * Одна фигура в системе координат слоя разметки.
 *
 * Доли умножаются на ширину слоя и его высоту: по горизонтали и вертикали
 * множители разные, потому что карта не квадратная. Толщины и кегли — от
 * ширины, иначе на вытянутой карте они бы разъезжались.
 */
function ShapeView({ shape, height }: { shape: TacticShape; height: number }): ReactNode {
  const x = (value: number): number => value * VIEW;
  const y = (value: number): number => value * height;

  if (shape.kind === 'stroke') {
    const points: string[] = [];
    for (let index = 0; index + 1 < shape.points.length; index += 2) {
      points.push(`${String(x(shape.points[index] ?? 0))},${String(y(shape.points[index + 1] ?? 0))}`);
    }

    return (
      <polyline
        points={points.join(' ')}
        fill="none"
        stroke={shape.color}
        strokeWidth={shape.width * VIEW}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray={
          shape.dashed
            ? `${String(Math.max(shape.width * VIEW * 2.2, 4))} ${String(Math.max(shape.width * VIEW * 2, 3.6))}`
            : undefined
        }
      />
    );
  }

  if (shape.kind === 'arrow') {
    const from = [x(shape.from[0]), y(shape.from[1])] as const;
    const to = [x(shape.to[0]), y(shape.to[1])] as const;
    const angle = Math.atan2(to[1] - from[1], to[0] - from[0]);
    const line = shape.width * VIEW;

    if (shape.variant === 'move') {
      // Заход рисуется шагами: пунктир читается как след состава, а сплошную
      // линию на карте легко спутать с дорогой или границей квартала.
      const step = Math.max(line * 2.4, 6);
      const head = Math.max(line * 4, VIEW * 0.02);
      const wing = Math.PI / 7;

      return (
        <g stroke={shape.color} fill={shape.color}>
          <line
            x1={from[0]}
            y1={from[1]}
            x2={to[0]}
            y2={to[1]}
            strokeWidth={line}
            strokeLinecap="round"
            strokeDasharray={`${String(step)} ${String(step * 0.8)}`}
          />
          <polygon
            points={[
              `${String(to[0])},${String(to[1])}`,
              `${String(to[0] - head * Math.cos(angle - wing))},${String(to[1] - head * Math.sin(angle - wing))}`,
              `${String(to[0] - head * Math.cos(angle + wing))},${String(to[1] - head * Math.sin(angle + wing))}`,
            ].join(' ')}
            stroke="none"
          />
        </g>
      );
    }

    // Огонь: тонкая сплошная линия и прицел на конце. Она не «идёт» — она
    // указывает, и по одному виду понятно, что это сектор обстрела.
    const thin = Math.max(line * 0.65, 1);
    const radius = Math.max(line * 2.2, VIEW * 0.006);

    return (
      <g stroke={shape.color} fill="none" strokeWidth={thin} strokeLinecap="round">
        <line x1={from[0]} y1={from[1]} x2={to[0]} y2={to[1]} />
        <circle cx={to[0]} cy={to[1]} r={radius} />
        <line x1={to[0] - radius * 1.7} y1={to[1]} x2={to[0] + radius * 1.7} y2={to[1]} />
        <line x1={to[0]} y1={to[1] - radius * 1.7} x2={to[0]} y2={to[1] + radius * 1.7} />
      </g>
    );
  }

  if (shape.kind === 'car') {
    const { length, width } = carSize(VIEW);
    const cx = x(shape.at[0]);
    const cy = y(shape.at[1]);
    // Пустая папка с картинками — рисуем машину кузовом, а не пропускаем её.
    const url = carImageUrl(shape.image);

    return (
      /*
       * Машина развёрнута носом к середине карты — туда, где точка капта.
       * Угол считается от положения, поэтому при перетаскивании она
       * доворачивается сама, и хранить его в фигуре не нужно.
       *
       * Экипаж рисуется не здесь, а слоем выше: плашки расставляются с
       * оглядкой друг на друга, и одной фигуре это решение не принять.
       */
      <g transform={`rotate(${String(carAngle(shape.at))} ${String(cx)} ${String(cy)})`}>
        {url === null ? (
          <rect
            x={cx - width * 0.2}
            y={cy - length * 0.42}
            width={width * 0.4}
            height={length * 0.84}
            rx={width * 0.12}
            fill={shape.color}
          />
        ) : (
          <image
            href={url}
            x={cx - width / 2}
            y={cy - length / 2}
            width={width}
            height={length}
            preserveAspectRatio="xMidYMid meet"
          />
        )}
      </g>
    );
  }

  const radius = VIEW * 0.018;

  return (
    <g>
      <circle cx={x(shape.at[0])} cy={y(shape.at[1])} r={radius} fill={shape.color} />
      <text
        x={x(shape.at[0])}
        y={y(shape.at[1])}
        fill={readableInk(shape.color)}
        fontSize={radius * 1.3}
        fontWeight={700}
        textAnchor="middle"
        dominantBaseline="central"
      >
        {shape.label}
      </text>
    </g>
  );
}

/**
 * Плашка с экипажем.
 *
 * Тёмная подложка и полоска цвета машины слева, от машины к плашке — поводок.
 * Поводок нужен, когда плашке не хватило места рядом и она отъехала: по нему
 * видно, чей это экипаж, а по одной близости — уже нет.
 */
function CarLabelView({ label, height }: { label: CarLabelBox; height: number }): ReactNode {
  const px = label.x * VIEW;
  const py = label.y * height;
  const width = label.width * VIEW;
  const box = label.height * height;
  const line = CAR_BOX.line * VIEW;
  const font = CAR_BOX.text * VIEW;
  // Чёрная машина на тёмной плашке была бы невидима — ей белый поводок.
  const accent = label.color === '#000000' ? '#ffffff' : label.color;

  return (
    <g>
      <line
        x1={label.from[0] * VIEW}
        y1={label.from[1] * height}
        x2={px + width / 2}
        y2={py + box / 2}
        stroke={accent}
        strokeWidth={VIEW * 0.0015}
        opacity={0.8}
      />
      <rect x={px} y={py} width={width} height={box} rx={CAR_LABEL.padY * VIEW * 1.5} fill="rgba(12, 16, 24, 0.82)" />
      <rect
        x={px}
        y={py}
        width={Math.max(VIEW * 0.003, 2)}
        height={box}
        rx={CAR_LABEL.padY * VIEW * 1.5}
        fill={accent}
      />

      {label.lines.map((name, index) => (
        <text
          key={index}
          x={px + CAR_LABEL.padX * VIEW}
          y={py + CAR_LABEL.padY * height + line * (index + 0.5)}
          fill="#ffffff"
          fontSize={font}
          fontWeight={600}
          textAnchor="start"
          dominantBaseline="central"
        >
          {name}
        </text>
      ))}
    </g>
  );
}
